import { ChangeDetectorRef, Component, Injector, Input, OnInit } from "@angular/core";
import { LogbookUtils } from "ng/src/app/core/logbook-utils";
import { Template, TemplateSegment, TemplateSegmentTypeId, TemplateTypeId, TemplateWithUsers } from "ng/src/app/templates/shared/template.model";
import { BaseComponent } from "ng/src/app/shared/base.component";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import { forkJoin, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { CommitableValueAlias, DerivedValueAlias, HasValue } from 'ng/src/app/core/value';
import { LogUser } from "ng/src/app/users/shared/user.model";
import { Utils } from "ng/src/app/core/utils";
import { TemplateProjections } from "app/shared/model/request-parameter.model";
import { LogService } from "ng/src/app/logs/log.service";
import { Log } from "ng/src/app/logs/log.model";

// @ts-ignore: Lint error that will be resolved on AJS rewrite
import IInjectorService = angular.auto.IInjectorService;

@Component({
	selector: 'lb-template-detail[template]',
	templateUrl: './template-detail.component.html',
	styleUrls: ['template-detail.component.scss']
})
export class TemplateDetailComponent extends BaseComponent implements OnInit {
	@Input()
	set template(val: TemplateWithUsers) {
		this._template = val;
		this.updateAccessUserText();
	}
	
	get template() {
		return this._template;
	}
	
	get templateType() {
		return this.template.templateType.id;
	}
	
	coCreators = new Array<LogUser>();
	hasSubTemplates = false;
	
	log?: Log;
	alertConfig?: ReturnType<TemplateDetailComponent['getTemplateAlertConfig']>;
	accessUserText = '';
	
	private _template!: TemplateWithUsers;
	
	private valueAliases: Partial<Record<keyof TemplateWithUsers, CommitableValueAlias<any>>> = {};
	private accessCommitableValue?: CommitableValueAlias<Pick<TemplateWithUsers, 'allowedUsers' | 'allowAllUsers' | 'id'>>;
	
	private _commentToShowAlias?: DerivedValueAlias<number | undefined, CommentToShowSetting>;
	private _maxNoteHeightAlias?: DerivedValueAlias<number | undefined, MaxNoteHeightSetting>;
	private _endCarryForwardDaysAlias?: DerivedValueAlias<number | undefined, EndCarryForwardDaysSetting>;
	
	constructor(
		private templateService: TemplateService,
		private logService: LogService,
		private changeDetectorRef: ChangeDetectorRef,
		injector: Injector
	) {
		super(injector);
	}
	
	readonly TemplateTypes = TemplateTypeId;
	
	ngOnInit(): void {
		this.refresh();
	}
	
	refresh() {
		return forkJoin({
			log: this.logService.getLogById(this.template.logId)
		})
			.subscribe(res => {
				this.log = res.log;
				this.alertConfig = this.getTemplateAlertConfig();
			});
	}
	
	saveValueAlias(type: keyof Template) {
		const alias = this.getValueAlias(type);
		
		return this.templateService.updateTemplate(this.template, {
			[type]: alias.value ?? null
		}, TemplateProjections.WITH_USERS)
			.pipe(
				catchError(err => {
					alias.discard();
					return throwError(err);
				})
			)
			.subscribe(t => {
				(this.template as any)[type] = t[type];
				alias.commit();
			});
	}
	
	saveDefaultCoCreators(before: LogUser[], after: LogUser[]) {
		const beforeMap = Utils.arrayToMap(before, l => l.id);
		const afterMap = Utils.arrayToMap(after, l => l.id);
		
		const toAdd = after.filter(l => !(l.id in beforeMap)).map(l => l.id);
		const toRemove = before.filter(l => !(l.id in afterMap)).map(l => l.id);
		
		const toPatch = {
			addToCollections: {
				defaultCoCreators: toAdd
			},
			removeFromCollections: {
				defaultCoCreators: toRemove
			}
		};
		
		return this.templateService.updateTemplate(this.template, toPatch, TemplateProjections.WITH_USERS)
			.subscribe(t => {
				this.template.defaultCoCreators = t.defaultCoCreators;
				this.updateTemplateCacheIfNeeded();
			});
	}
	
	getCommentsToShowSetting(): HasValue<CommentToShowSetting> {
		if (this._commentToShowAlias) {
			return this._commentToShowAlias;
		}
		
		const alias = this.getValueAlias('commentsToShow')
			.derived<CommentToShowSetting>({
				get: (val) => {
					if (val == undefined) return 'Default';
					if (val === 0) return 'All';
					return 'Specific';
				},
				set: (val, aliasVal) => {
					if (val === 'Default') return undefined;
					if (val === 'All') return 0;
					
					if (this.isValidNumber(aliasVal) && aliasVal > 0) {
						return aliasVal;
					}
					
					return 1;
				}
			});
		
		return this._commentToShowAlias = alias;
	}
	
	getMaxNoteHeightSetting(): HasValue<MaxNoteHeightSetting> {
		if (this._maxNoteHeightAlias) {
			return this._maxNoteHeightAlias;
		}
		
		const alias = this.getValueAlias('maxNoteHeight')
			.derived<MaxNoteHeightSetting>({
				get: (val) => {
					if (val == undefined) return 'Default';
					if (val === 0) return 'Unlimited';
					return 'Specific';
				},
				set: (val, aliasVal) => {
					if (val === 'Default') return undefined;
					if (val === 'Unlimited') return 0;
					
					if (this.isValidNumber(aliasVal) && aliasVal > 0) {
						return aliasVal;
					}
					
					return 1;
				}
			});
		
		return this._maxNoteHeightAlias = alias;
	}
	
	getEndCarryForwardDaysSetting(): HasValue<EndCarryForwardDaysSetting> {
		if (this._endCarryForwardDaysAlias) {
			return this._endCarryForwardDaysAlias;
		}
		
		const alias = this.getValueAlias('endCarryForwardDays')
			.derived<EndCarryForwardDaysSetting>({
				get: (val) => {
					if (val == undefined) return 'None';
					if (val === 0) return 'End of Day';
					return 'Specific';
				},
				set: (val, aliasVal) => {
					if (val === 'None') return undefined;
					if (val === 'End of Day') return 0;
					
					if (this.isValidNumber(aliasVal) && aliasVal > 0) {
						return aliasVal;
					}
					
					return 1;
				}
			});
		
		return this._endCarryForwardDaysAlias = alias;
	}
	
	getValueAlias<Type extends keyof Template>(type: Type): CommitableValueAlias<TemplateWithUsers[Type]> {
		if (type in this.valueAliases) return this.valueAliases[type]!;
		
		const value = new CommitableValueAlias(
			() => this.template[type],
			(val) => this.template[type] = val
		);
		
		value.addEventListener('commit', () => {
			this.updateTemplateCacheIfNeeded();
		});
		
		return this.valueAliases[type] = value;
	}
	
	getAccessAlias(): NonNullable<TemplateDetailComponent['accessCommitableValue']> {
		if (this.accessCommitableValue && this.accessCommitableValue.value.id === this.template.id) {
			return this.accessCommitableValue;
		}
		
		delete this.accessCommitableValue;
		
		const templateId = this.template.id;
		return this.accessCommitableValue = new CommitableValueAlias(
			() => ({
				id: templateId,
				allowedUsers: Array.from(this.template.allowedUsers),
				allowAllUsers: this.template.allowAllUsers
			}),
			(val) => {
				this.template.allowedUsers = val.allowedUsers;
				this.template.allowAllUsers = val.allowAllUsers;
			}
		)
			.addEventListener('commit', (value) => {
				this.updateAccessUserText();
				this.updateTemplateCacheIfNeeded();
			});
	}
	
	startEditingUsers() {
		this.coCreators = [...this.template.defaultCoCreators];
	}
	
	saveAccessValue() {
		const value = this.getAccessAlias();
		
		if (!value.hasChanges()) return;
		const update = {
			allowAllUsers: value.value.allowAllUsers,
			...LogbookUtils.mapCollectionUpdateToIds(
				LogbookUtils.collectionUpdate(value.value.allowedUsers, this.template.allowedUsers, 'allowedUsers')
			)
		};
		
		return this.templateService.updateTemplate(this.template, update).subscribe(
			() => value.commit(),
			() => value.discard()
		);
	}
	
	private updateTemplateCacheIfNeeded() {
		if (LogService.selectedLog?.id === this.template.logId) {
			LogService.refreshTemplates();
		}
	}
	
	private updateAccessUserText() {
		return this.accessUserText = this.template.allowedUsers.map(usr => `${usr.user.firstName} ${usr.user.lastName}`).join(', ');
	}
	
	private getTemplateAlertConfig() {
		const self = this;
		
		const alertModule: any = require('../../../../../app/alerts/Alerts');
		const ajsInjector: IInjectorService = this.injector.get('$injector');
		const $http = ajsInjector.get('$http');
		const $q = ajsInjector.get('$q');
		
		return {
			config: {
				name: {hidden: true},
				parameters: {hidden: true},
				notification: {hidden: true},
				notifyUsers: {
					forceClear: true,
					header: 'Notifications',
					description: 'Notify users when new note is entered'
				},
				missingDataCheck: {
					hidden: false
				},
				missingDataCheckNotification: {
					hidden: true
				},
				preSaveTransformer: function (alert: any) {
					alert.subject = 'New {{TemplateName}} {{AlertKeyFields}}';
					alert.clearMessage = '{{Note}}'
					
					alert.missingDataSubject = 'Missing {{TemplateName}} {{AlertKeyFields}}'
					alert.missingDataMessage = 'An expected log entry for {{TemplateName}} {{AlertKeyFields}} was not entered.' +
						'{{#if [NoteUpdated]}}  \nThe last entry for this was at {{NoteUpdated}}.{{/if}}'
					
					alert.setNewNotesOnly(true);
				}
			},
			getAlert: function()
			{
				return $q(function(resolve: any, reject: any)
				{
					$q.all([$http.get('/api/alerts', {
							params: {
								log: self.log!.id,
								isDraft: true,
								saveInProgress: false,
								'complexObject.forTemplate': self.template.id
							}
						}),
							$http.get('/api/alerts', {
								params: {
									log: self.log!.id,
									activeRevision: true,
									saveInProgress: false,
									'complexObject.forTemplate': self.template.id
								}
							}),
							$http.get('/api/alertObjects', {
								params: {
									saveInProgress: false,
									forTemplate: self.template.id
								}
							})])
						.then(function(results: any)
						{
							let baseAlert = results[0].data._embedded.alerts[0] || results[1].data._embedded.alerts[0];
							
							baseAlert = alertModule.Alerts.createAlert(self.log!.id, baseAlert);
							baseAlert.template = self.template.id;
							baseAlert.complexObject = results[2].data._embedded.alertObjects[0];
							baseAlert.setName('New Note Notification ' + self.template.id);
							
							resolve(baseAlert);
						}, function(error: any)
						{
							reject(error)
						})
				});
			},
			draftSaveMessage: "Saving Notification Settings Draft",
			publishSaveMessage: "Publishing Notification Settings",
			navigateHaltMessage: "You have made changes to your notification preferences without saving. Are you sure you want to leave?"
		}
	}
}

type CommentToShowSetting = 'Default' | 'All' | 'Specific';
type MaxNoteHeightSetting = 'Default' | 'Unlimited' | 'Specific';
type EndCarryForwardDaysSetting = 'None' | 'End of Day' | 'Specific';